import React from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Divider,
  Chip,
  Fab,
} from "@mui/material";
import {
  Close as CloseIcon,
  Route as RouteIcon,
  Delete as DeleteIcon,
  ClearAll as ClearAllIcon,
  LocationOn as LocationOnIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";

const RoutePlanner = ({
  selectedLocations,
  onRemoveLocation,
  onClearAll,
  onOpenInGoogleMaps,
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        right: { xs: "16px", sm: "24px" },
        top: { xs: "100px", sm: "120px" },
        width: { xs: "calc(100% - 32px)", sm: "380px", md: "420px" },
        maxWidth: "420px",
        maxHeight: { xs: "calc(100vh - 140px)", sm: "calc(100vh - 160px)" },
        backgroundColor: "#FFFFFF",
        borderRadius: "16px",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
        border: "1px solid rgba(196, 30, 58, 0.1)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: { xs: "16px", sm: "20px" },
          backgroundColor: "#C41E3A",
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <RouteIcon sx={{ fontSize: "28px" }} />
          <Typography
            variant="h6"
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: { xs: "1.1rem", sm: "1.25rem" },
            }}
          >
            Plan Your Route
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: { xs: "16px", sm: "20px" },
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {/* Instructions */}
        {selectedLocations.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#999",
            }}
          >
            <LocationOnIcon
              sx={{ fontSize: "64px", color: "#E0E0E0", marginBottom: "16px" }}
            />
            <Typography
              variant="body1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "1rem",
                marginBottom: "8px",
                color: "#666",
              }}
            >
              No locations selected
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.875rem",
                color: "#999",
                lineHeight: 1.6,
              }}
            >
              Click on map markers to add locations to your route
            </Typography>
          </Box>
        ) : (
          <>
            {/* Selected Locations List */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontWeight: 600,
                    color: "#333",
                    fontSize: "1rem",
                  }}
                >
                  Selected Locations ({selectedLocations.length})
                </Typography>
                {selectedLocations.length > 0 && (
                  <Button
                    onClick={onClearAll}
                    size="small"
                    startIcon={<ClearAllIcon />}
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      color: "#C41E3A",
                      textTransform: "none",
                      fontSize: "0.75rem",
                      padding: "4px 8px",
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "rgba(196, 30, 58, 0.1)",
                      },
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              <List
                sx={{
                  padding: 0,
                  "& .MuiListItem-root": {
                    padding: "12px",
                    marginBottom: "8px",
                    backgroundColor: "#FAFAFA",
                    borderRadius: "8px",
                    border: "1px solid #E0E0E0",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                      borderColor: "#C41E3A",
                      transform: "translateX(4px)",
                    },
                  },
                }}
              >
                {selectedLocations.map((location, index) => (
                  <ListItem key={location.id || index}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          backgroundColor: "#C41E3A",
                          color: "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "'Playfair Display', serif",
                          fontWeight: 700,
                          fontSize: "0.875rem",
                        }}
                      >
                        {index + 1}
                      </Box>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "'Playfair Display', serif",
                              fontWeight: 600,
                              color: "#333",
                              fontSize: "0.9rem",
                            }}
                          >
                            {location.name || location.address || "Location"}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: "'Inter', sans-serif",
                              color: "#666",
                              fontSize: "0.75rem",
                              display: "block",
                              marginTop: "4px",
                            }}
                          >
                            {location.address && location.name
                              ? location.address
                              : location.category
                              ? location.category.charAt(0).toUpperCase() +
                                location.category.slice(1)
                              : ""}
                          </Typography>
                        }
                        sx={{ flex: 1, margin: 0 }}
                      />
                      <IconButton
                        onClick={() => onRemoveLocation(location.id || index)}
                        size="small"
                        sx={{
                          color: "#C41E3A",
                          "&:hover": {
                            backgroundColor: "rgba(196, 30, 58, 0.1)",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </Box>
          </>
        )}
      </Box>

      {/* Footer Actions */}
      {selectedLocations.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              padding: { xs: "16px", sm: "20px" },
              backgroundColor: "#FAFAFA",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={onOpenInGoogleMaps}
              startIcon={<OpenInNewIcon />}
              sx={{
                backgroundColor: "#4CAF50",
                color: "#FFFFFF",
                fontFamily: "'Playfair Display', serif",
                fontWeight: 600,
                fontSize: "1rem",
                padding: "12px 24px",
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                "&:hover": {
                  backgroundColor: "#45a049",
                  boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Open in Google Maps
            </Button>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "'Inter', sans-serif",
                color: "#666",
                fontSize: "0.75rem",
                textAlign: "center",
                padding: "4px",
                fontStyle: "italic",
              }}
            >
              {selectedLocations.length === 1 
                ? "View location in Google Maps"
                : `Plan route with ${selectedLocations.length} locations in Google Maps`}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default RoutePlanner;

